import React, { useEffect, useState, forwardRef, Ref } from 'react';
import { Button as MaterialUiButton, ButtonProps } from '@mui/material';
import { ISharedProps } from '../../tools/ISharedProps';
import classNames from 'classnames';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_MEDIUM, FONT_SIZE_SMALL, FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { PRIMARY_BLUE, WHITE } from '~/Constants/Colors.constants';
import { Dots } from '~/UIKit';

export interface IButtonProps extends ButtonProps, ISharedProps {
  isLoading?: boolean;
  withGracefulDelay?: boolean;
}

const useStyles = makeStyles({
  defaultSmall: {
    height: 28,
    fontSize: FONT_SIZE_SMALL,
    lineHeight: 1,
    borderRadius: '3px',
  },
  defaultMedium: {
    height: 35,
    fontSize: FONT_SIZE_SMALLISH,
    letterSpacing: 0.5,
    lineHeight: '19px',
    minWidth: 128,
    borderRadius: '3px',
  },
  defaultLarge: {
    height: 50,
    fontSize: FONT_SIZE_MEDIUM,
    letterSpacing: 0.5,
    lineHeight: '34px',
    borderRadius: '3px',
  },
});

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    { isLoading, withGracefulDelay, className, size, color, ...props }: IButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const { classes } = useStyles();
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const isDisabled = isButtonLoading || props.disabled;

    const defaultSize =
      size === 'small'
        ? classes.defaultSmall
        : size === 'medium'
        ? classes.defaultMedium
        : size === 'large'
        ? classes.defaultLarge
        : '';
    const dotColor = color === 'primary' ? WHITE : PRIMARY_BLUE;

    useEffect(() => {
      if (!withGracefulDelay || !isLoading) {
        setIsButtonLoading(isLoading || false);
      } else {
        setTimeout(() => {
          setIsButtonLoading(false);
        }, ((Math.random() * 10000) % 2000) + 3000);
      }
    }, [isLoading]);

    return (
      <MaterialUiButton
        color={color ?? 'primary'}
        disabled={isDisabled}
        className={classNames(defaultSize, className)}
        size={size}
        ref={ref}
        {...props}
      >
        {isButtonLoading ? <Dots color={dotColor} /> : props.children}
      </MaterialUiButton>
    );
  },
);

Button.displayName = 'Button';
